/**
 * Relevé en « accès léger » (2026-09-12).
 *
 * Un étudiant qui ne doit que des périodes cochées en accès léger voit sa
 * décision : semestre validé, ou non validé avec les matières à rattraper par
 * UE — sans aucune note, moyenne, crédit ni rang. Le reste du relevé s'ouvre
 * dès la régularisation des périodes listées.
 */
import React from 'react';
import { CheckCircle2, XCircle, Lock } from 'lucide-react';
import { ReleveLeger } from '../services/portailApi';

function formatMoney(s: string | null | undefined): string {
  const n = parseFloat(s || '0');
  if (isNaN(n)) return '';
  return new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';
}

export const ReleveLegerVue: React.FC<{ data: ReleveLeger }> = ({ data }) => {
  const dues = data.periodes_dues || [];
  const ues = data.ues_a_rattraper || [];
  const nbMatieres = ues.reduce((n, ue) => n + ue.matieres.length, 0);

  return (
    <div className="space-y-3">
      {!data.decision_disponible ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 text-center text-sm text-gray-300">
          {data.message || 'Les résultats de ce semestre ne sont pas encore disponibles.'}
        </div>
      ) : data.valide ? (
        <div className="bg-green-900/20 border border-green-800/60 rounded-xl p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
          <div className="text-lg font-semibold text-green-200">Semestre {data.semestre} validé</div>
          <p className="text-sm text-green-300/80 mt-1">
            Vous n'avez aucune matière à rattraper pour ce semestre.
          </p>
        </div>
      ) : (
        <div className="bg-orange-900/20 border border-orange-800/60 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-orange-800/60 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <div>
              <div className="font-semibold text-orange-200">Semestre {data.semestre} non validé</div>
              <div className="text-xs text-orange-300/80 mt-0.5">
                {nbMatieres > 0
                  ? `${nbMatieres} matière${nbMatieres > 1 ? 's' : ''} à rattraper`
                  : 'Rapprochez-vous de la scolarité pour connaître les matières à rattraper.'}
              </div>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {ues.map((ue) => (
              <div key={ue.code_ue || ue.nom_ue}>
                <div className="text-xs font-semibold text-gray-200 flex items-center gap-2 flex-wrap">
                  <span>{ue.nom_ue}</span>
                  {ue.prioritaire && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/50 text-red-300 font-medium">
                      prioritaire
                    </span>
                  )}
                </div>
                {ue.matieres.length > 0 ? (
                  <ul className="mt-1.5 space-y-1">
                    {ue.matieres.map((m) => (
                      <li key={m.code_matiere || m.nom_matiere} className="text-sm text-gray-300 flex gap-2">
                        <span className="text-orange-400">•</span>
                        <span>{m.nom_matiere}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-gray-500 mt-1">
                    Aucune matière identifiée individuellement dans cette UE.
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gray-900 border border-amber-800/50 rounded-xl p-4">
        <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold">
          <Lock size={16} /> Notes et moyennes masquées
        </div>
        <p className="text-xs text-gray-400 mt-1">
          Votre relevé complet (notes, moyennes, crédits) sera visible dès la régularisation de :
        </p>
        <ul className="mt-2 space-y-1">
          {dues.map((d) => (
            <li key={d.periode} className="text-xs text-gray-200 flex justify-between gap-2">
              <span>{d.label}</span>
              <span className="text-amber-300">
                {d.present && d.solde && parseFloat(d.solde) > 0 ? `Reste ${formatMoney(d.solde)}` : 'Non enregistré'}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
